/**
 * A pressure test's chart photo (PT-3): shown, and — on the field screen —
 * taken.
 *
 * READING IT BACK. The test carries `chart.file_key`; the photo is the
 * 'pt_test_chart' upload with that key, listed by
 * GET /uploads/pt_test_chart/:testId. The image loads through the
 * authenticated download door (`/uploads/download/:id?dt=`), never a presigned
 * R2 URL held in state — those expire and a stale one renders as a broken
 * image that reads as "no chart". The token lives ≤120s and is minted when the
 * photo is first drawn.
 *
 * THREE STATES, NEVER TWO: no chart on the test (say so), a chart whose image
 * could not be loaded (say so — the chart IS attached, and the API still counts
 * it for the pass), and the photo.
 *
 * TAKING IT is online-only (useAttachPtChart explains why: the offline queue
 * cannot claim a chart onto a test). Offline, the button is replaced by a line
 * saying it needs signal — never a queued photo that silently never attaches.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Camera, ImageOff, Loader2 } from 'lucide-react';
import { api, uploadDownloadUrl } from '../../api/client';
import { useAttachPtChart, usePtChartUpload } from '../../hooks/usePressureTesting';
import { formatInstant } from '../../lib/calendar-date';
import { useSyncStore } from '../../lib/offline/sync-store';
import type { PtTest } from '../../types/pressure-testing';
import { ptWriteError } from './PtJobDialogs';

/** The chart image, or the reason there is none. */
export function PtChartImage({ test, timeZone, className = '' }: { test: Pick<PtTest, 'id' | 'chart'>; timeZone: string | null; className?: string }) {
  const fileKey = test.chart?.file_key ?? null;
  const upload = usePtChartUpload(test.id, fileKey);
  const uploadId = upload.data?.id ?? null;
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    setFailed(false);
    if (!uploadId) return undefined;
    api.uploads
      .downloadToken(uploadId)
      .then((res) => {
        const token = (res.data as { data?: { token?: unknown } } | null)?.data?.token;
        if (cancelled) return;
        if (typeof token === 'string' && token) setSrc(uploadDownloadUrl(uploadId, token));
        else setFailed(true);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [uploadId]);

  if (!fileKey) {
    return (
      <p className={`text-sm text-fieldiq-text ${className}`} data-testid="pt-chart-none">
        No chart photo attached.
      </p>
    );
  }
  const when = formatInstant(test.chart?.uploaded_at, timeZone);
  const caption = <p className="mt-1 text-xs text-fieldiq-muted">Chart attached{when ? ` · ${when}` : ''}</p>;
  // The list answered and holds no row with this key, or the token / image failed.
  if (failed || upload.isError || (upload.isSuccess && !uploadId)) {
    return (
      <div className={className} data-testid="pt-chart-unloadable">
        <p className="flex items-center gap-2 text-sm text-fieldiq-text">
          <ImageOff className="size-4 shrink-0 text-fieldiq-muted" aria-hidden="true" />
          A chart photo is attached, but its image could not be loaded.
        </p>
        {caption}
      </div>
    );
  }
  return (
    <div className={className}>
      {src ? (
        <a href={src} target="_blank" rel="noreferrer" className="block">
          <img
            src={src}
            alt="Chart recorder chart"
            data-testid="pt-chart-image"
            className="max-h-72 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark object-contain"
            onError={() => setFailed(true)}
          />
        </a>
      ) : (
        <div className="flex h-32 items-center justify-center rounded-fi-control border border-fieldiq-border bg-fieldiq-dark">
          <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading the chart photo" />
        </div>
      )}
      {caption}
    </div>
  );
}

/** Take (or replace) the chart photo. Online only. */
export function PtChartCapture({ test, buttonClassName }: { test: Pick<PtTest, 'id' | 'chart'>; buttonClassName: string }) {
  const isOnline = useSyncStore((s) => s.isOnline);
  const attach = useAttachPtChart();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    attach.mutate(
      { testId: test.id, file },
      {
        onError: (err) => {
          const fromApi = ptWriteError(err, '').message;
          const own = (err as { userMessage?: unknown; message?: unknown }) ?? {};
          setError(
            fromApi ||
              (typeof own.userMessage === 'string' && own.userMessage) ||
              (typeof own.message === 'string' && own.message) ||
              'The chart photo was not attached. Try again.',
          );
        },
      },
    );
  };

  if (!isOnline) {
    return (
      <p
        role="status"
        data-testid="pt-chart-offline"
        className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text"
      >
        Needs signal to upload the chart photo. It is not saved for later — take it when you are back in coverage.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <input ref={input} type="file" accept="image/*" capture="environment" className="sr-only" onChange={onFile} data-testid="pt-chart-input" tabIndex={-1} aria-hidden="true" />
      <button type="button" className={buttonClassName} onClick={() => input.current?.click()} disabled={attach.isPending} data-testid="pt-chart-capture">
        {attach.isPending ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : <Camera className="size-5" aria-hidden="true" />}
        {attach.isPending ? 'Uploading chart…' : test.chart ? 'Replace chart photo' : 'Photograph chart'}
      </button>
      {error && (
        <p role="alert" data-testid="pt-chart-error" className="rounded-fi-control border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
          {error}
        </p>
      )}
    </div>
  );
}
